import { IconChevronDownSmall } from 'central-icons/IconChevronDownSmall';
import { IconCrossSmall } from 'central-icons/IconCrossSmall';
import classNames from 'classnames';
import {
  type CSSProperties,
  type FocusEvent,
  type ReactNode,
  useCallback,
  useContext,
  useState,
} from 'react';
import ReactSelect, {
  type ClassNamesConfig,
  type ClearIndicatorProps,
  type ControlProps,
  components,
  type DropdownIndicatorProps,
  type GroupBase,
  type Props as ReactSelectProps,
} from 'react-select';

import { UIPortalContext } from '../ui-theme/UITheme';
import styles from './Select.module.css';

type Size = 'sm' | 'md' | 'lg';

export type SelectCssVar =
  | '--select-disabled-bg'
  | '--select-disabled-border'
  | '--select-disabled-color';

export type SelectProps<
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
> = Omit<
  ReactSelectProps<Option, IsMulti, Group>,
  'className' | 'classNames'
> & {
  startSlot?: ReactNode;
  size?: Size;
  className?: string;
  isInvalid?: boolean;
  style?: CSSProperties;
  cssVars?: Partial<Record<SelectCssVar, string>>;
  'data-testid'?: string;
};

type SelectControlProps<
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
> = ControlProps<Option, IsMulti, Group> & {
  selectProps: ControlProps<Option, IsMulti, Group>['selectProps'] &
    Pick<SelectProps<Option, IsMulti, Group>, 'startSlot'>;
};

const ClearIndicator = <
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  props: ClearIndicatorProps<Option, IsMulti, Group>
) => (
  <components.ClearIndicator {...props}>
    <IconCrossSmall size={16} />
  </components.ClearIndicator>
);

const DropdownIndicator = <
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  props: DropdownIndicatorProps<Option, IsMulti, Group>
) => (
  <components.DropdownIndicator {...props}>
    <IconChevronDownSmall size={16} />
  </components.DropdownIndicator>
);

const IndicatorSeparator = () => null;

const Control = <
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>({
  children,
  selectProps,
  ...props
}: SelectControlProps<Option, IsMulti, Group>) => (
  <components.Control {...props} selectProps={selectProps}>
    {selectProps.startSlot ? (
      <span className={styles.startSlot} aria-hidden="true">
        {selectProps.startSlot}
      </span>
    ) : null}
    {children}
  </components.Control>
);

export const Select = <
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>({
  size = 'md',
  startSlot,
  className,
  isInvalid,
  style,
  cssVars,
  components: componentOverrides,
  styles: styleOverrides,
  'data-testid': dataTestId,
  menuPlacement = 'auto',
  maxMenuHeight = 205,
  onFocus,
  onBlur,
  onMenuOpen,
  onMenuClose,
  ...selectProps
}: SelectProps<Option, IsMulti, Group>) => {
  const menuPortalTarget = useContext(UIPortalContext);
  const [focused, setFocused] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const reactSelectProps = {
    ...selectProps,
    startSlot,
  } as ReactSelectProps<Option, IsMulti, Group> & {
    startSlot?: ReactNode;
  };

  const handleFocus = useCallback(
    (e: FocusEvent<HTMLInputElement>) => {
      setFocused(true);
      onFocus?.(e);
    },
    [onFocus]
  );

  const handleBlur = useCallback(
    (e: FocusEvent<HTMLInputElement>) => {
      setFocused(false);
      onBlur?.(e);
    },
    [onBlur]
  );

  const handleMenuOpen = useCallback(() => {
    setMenuOpen(true);
    onMenuOpen?.();
  }, [onMenuOpen]);

  const handleMenuClose = useCallback(() => {
    setMenuOpen(false);
    onMenuClose?.();
  }, [onMenuClose]);

  const selectClassNames: ClassNamesConfig<Option, IsMulti, Group> = {
    control: () => styles.control ?? '',
    valueContainer: () => styles.valueContainer ?? '',
    placeholder: () => styles.placeholder ?? '',
    singleValue: () => styles.singleValue ?? '',
    input: () => styles.input ?? '',
    indicatorsContainer: () => styles.indicatorsContainer ?? '',
    clearIndicator: () => styles.clearIndicator ?? '',
    dropdownIndicator: () => styles.dropdownIndicator ?? '',
    menu: () => styles.menu ?? '',
    menuList: () => styles.menuList ?? '',
    option: (state) =>
      classNames(
        styles.option,
        state.isFocused && styles.optionFocused,
        state.isSelected && styles.optionSelected,
        state.isDisabled && styles.optionDisabled
      ),
    noOptionsMessage: () => styles.message ?? '',
    loadingMessage: () => styles.message ?? '',
    multiValue: () => styles.multiValue ?? '',
    multiValueLabel: () => styles.multiValueLabel ?? '',
    multiValueRemove: () => styles.multiValueRemove ?? '',
  };

  return (
    <div
      className={classNames(styles.wrapper, styles[size], className)}
      style={{ ...style, ...(cssVars as CSSProperties) }}
      data-testid={dataTestId}
      data-focused={focused || undefined}
      data-disabled={selectProps.isDisabled || undefined}
      data-invalid={isInvalid || undefined}
      data-open={menuOpen || undefined}
    >
      <ReactSelect<Option, IsMulti, Group>
        unstyled
        menuPortalTarget={menuPortalTarget}
        maxMenuHeight={maxMenuHeight}
        menuPlacement={menuPlacement}
        components={{
          Control,
          ClearIndicator,
          DropdownIndicator,
          IndicatorSeparator,
          ...componentOverrides,
        }}
        classNames={selectClassNames}
        styles={{
          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
          ...styleOverrides,
        }}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onMenuOpen={handleMenuOpen}
        onMenuClose={handleMenuClose}
        {...reactSelectProps}
      />
    </div>
  );
};
